// Feature flags + the hidden QA panel's state (features 10/16, alvin: "test a feature without doing the real steps").
// One switch per new feature from all three lanes. Every lane reads its feature through flag() / useFlag(); nothing else.
// Production always gets the default below (the shipped behavior). Only a verified QA session (?qa=<QA_KEY>, checked by
// the `qa` function; App.tsx keeps the key) can override a flag, set a "pretend today is" date or turn on a scenario
// preset, and every override shows the "QA mode" badge. Presets only change what this device renders: nothing here
// writes to the database.
import { useSyncExternalStore } from 'react'

export type Lane = 'browse' | 'growth' | 'hosts' | 'taste' | 'filters' | 'messaging' | 'itinerary' | 'brand'
type FlagDef = { lane: Lane; label: string; on: boolean }

/** Defaults = what production ships. The QA panel lists them in this order. Labels are internal (QA panel only). */
export const FLAGS = {
  // Browse lane (claude/feat-browse)
  words_filter: { lane: 'browse', label: 'Words filter: include / exclude (R-94)', on: true },
  fame_filter: { lane: 'browse', label: 'Fame filter: well known / local / under the radar (R-08)', on: true },
  map_view: { lane: 'browse', label: 'Map view on Browse (R-06)', on: true },
  ask_browse: { lane: 'browse', label: 'Ask in your own words on Browse (R-05)', on: true },
  go_with_others: { lane: 'browse', label: 'Go with others label (R-30)', on: true },
  trips: { lane: 'browse', label: 'Trips across cities (R-42)', on: false },
  // Growth lane (claude/feat-growth)
  install_hint: { lane: 'growth', label: 'Add to home screen hint (R-59)', on: true },
  weekend: { lane: 'growth', label: 'This weekend, for you', on: true },
  come_with: { lane: 'growth', label: 'Come with me? link', on: true },
  plus_offer: { lane: 'growth', label: 'Plus offer card (pay test, off until weeks 5-6)', on: false },
  plus_checkout: { lane: 'growth', label: 'Plus: join with Stripe (R-46, test keys only)', on: false },
  affiliate_links: { lane: 'growth', label: 'Affiliate links with disclosure (R-47)', on: false },
  plan_tips: { lane: 'growth', label: 'Why-this-helps tips in Create a plan (R-15)', on: true },
  // Hosts lane (claude/feat-hosts)
  host_submit: { lane: 'hosts', label: 'Send us your event (R-31)', on: true },
  host_review: { lane: 'hosts', label: 'Host review queue (R-58)', on: true },
  // Taste lane (claude/feat-taste)
  taste_space: { lane: 'taste', label: 'Taste profile space (/taste) + voice interview', on: true },
  // Round 3, Lane 4 (claude/r3-messaging-amow14): OFF in production; the server also needs MESSAGING_MODE (off by default)
  messaging: { lane: 'messaging', label: 'Emails and texts in Settings (R-17, R-48, R-26, R-49)', on: false },
  // Round 3, Lane 3 (claude/r3-filters-calendar)
  time_filter: { lane: 'filters', label: 'Time of day and weekday filter (R-103)', on: true },
  booking_note: { lane: 'filters', label: 'Booking note on a detail (by appointment, book ahead)', on: true },
  calendar_memory: { lane: 'filters', label: 'Remember their calendar on the profile (R-24)', on: true },
  // Round 4 (claude/r4-itinerary): ON in production (alvin 10/22 22:34Z: "turn on itinerary_v2 in prod")
  itinerary_v2: { lane: 'itinerary', label: 'Itinerary calendar view + your own blocks (R4)', on: true },
  // Round 4 brand (claude/r4-golden-hour): OFF in production until alvin turns it on (approved 10/16 19:13Z)
  golden_hour: { lane: 'brand', label: 'Golden hour look: warm palette, Bricolage titles, soft filled shapes (R4)', on: true },
} as const satisfies Record<string, FlagDef>

export type FlagName = keyof typeof FLAGS

/** Scenario presets: render the app as if this were true, on this device only. */
export const PRESETS = {
  none: 'No preset',
  checkin: 'Check-in pad ("did you go?") with a sample outing',
  first_add: 'First calendar add done (install hint)',
  third_add: '3rd calendar add done (Plus card)',
  come_pending: 'A pending Come with me invite',
} as const
export type Preset = keyof typeof PRESETS
export type Quadrant = 'tl' | 'tr' | 'bl' | 'br' | 'mid'

export type QaState = {
  /** true once the `qa` function accepted the kept key in this browser session */
  on: boolean
  flags: Partial<Record<FlagName, boolean>>
  /** "pretend today is": an ISO date-time, or null for the real clock */
  now: string | null
  preset: Preset
  /** check-in preset: where the dot starts and how many calendar adds to pretend */
  quadrant: Quadrant
  /** the pad's up/down question (lib/checkinCore.ts PAD_AXES) */
  axis: 'connection' | 'again' | 'worth' | 'learned' | 'challenge'
  adds: number
}

// not mg_*: lib/owner.ts clears mg_* keys when the person changes, and QA mode belongs to the device (like the review key)
const KEY = 'magellan-qa'         // localStorage: the kept key (App.tsx writes it from ?qa=)
const OK = 'magellan-qa-ok'       // sessionStorage: the key the server accepted in this session
const STATE = 'magellan-qa-state' // localStorage: overrides, kept between reloads while QA mode is on
const OFF: QaState = { on: false, flags: {}, now: null, preset: 'none', quadrant: 'tr', axis: 'connection', adds: 0 }

const ls = {
  get: (k: string) => { try { return localStorage.getItem(k) } catch { return null } },
  set: (k: string, v: string | null) => { try { if (v === null) localStorage.removeItem(k); else localStorage.setItem(k, v) } catch { /* private mode */ } },
}
const ss = {
  get: (k: string) => { try { return sessionStorage.getItem(k) } catch { return null } },
  set: (k: string, v: string | null) => { try { if (v === null) sessionStorage.removeItem(k); else sessionStorage.setItem(k, v) } catch { /* private mode */ } },
}

function load(): QaState {
  const key = ls.get(KEY)
  if (!key || ss.get(OK) !== key) return OFF
  try {
    const s = JSON.parse(ls.get(STATE) ?? '{}') as Partial<QaState>
    return { ...OFF, ...s, on: true }
  } catch {
    return { ...OFF, on: true }
  }
}

let state: QaState = typeof window === 'undefined' ? OFF : load()
const subs = new Set<() => void>()
const emit = () => subs.forEach((f) => f())
const subscribe = (f: () => void) => { subs.add(f); return () => { subs.delete(f) } }

/** Is this feature on? Production: the default. A verified QA session: its override, if any. */
export function flag(name: FlagName): boolean {
  const o = state.on ? state.flags[name] : undefined
  return typeof o === 'boolean' ? o : FLAGS[name].on
}
export function useFlag(name: FlagName): boolean {
  return useSyncExternalStore(subscribe, () => flag(name), () => FLAGS[name].on)
}

/** The QA state (OFF outside a verified QA session). */
export const qaState = (): QaState => state
export const useQa = (): QaState => useSyncExternalStore(subscribe, qaState, () => OFF)
/** Any override in force (the badge says "QA mode" whenever the panel is on; this says whether anything is changed). */
export const qaOverriding = (s: QaState = state) => s.on && (Object.keys(s.flags).length > 0 || s.now !== null || s.preset !== 'none')

/** The clock features read: the "pretend today is" date in a QA session, else the real time. */
export function qaNow(): Date {
  if (state.on && state.now) {
    const d = new Date(state.now)
    if (!Number.isNaN(d.getTime())) return d
  }
  return new Date()
}
/** The active preset (always 'none' outside a verified QA session). */
export const qaPreset = (): Preset => (state.on ? state.preset : 'none')

export function setQa(patch: Partial<Omit<QaState, 'on'>>) {
  if (!state.on) return
  state = { ...state, ...patch }
  const { on: _on, ...kept } = state
  void _on
  ls.set(STATE, JSON.stringify(kept))
  emit()
}
export function setFlag(name: FlagName, v: boolean | null) {
  const flags = { ...state.flags }
  if (v === null || v === FLAGS[name].on) delete flags[name]
  else flags[name] = v
  setQa({ flags })
}
export function resetQa() { setQa({ flags: {}, now: null, preset: 'none', quadrant: 'tr', axis: 'connection', adds: 0 }) }

// ---------- the gate (App.tsx) ----------
/** ?qa=<key> keeps the key on this device, ?qa=off forgets it and every override; the parameter leaves the address bar. */
export function keepQaKey(): string | null {
  try {
    const u = new URL(location.href)
    const v = u.searchParams.get('qa')
    if (v !== null) {
      if (v && v !== 'off') ls.set(KEY, v)
      else endQa()
      u.searchParams.delete('qa')
      history.replaceState(history.state, '', u.pathname + u.search + u.hash)
    }
  } catch { /* no URL access: nothing kept */ }
  return ls.get(KEY)
}
/** The `qa` function accepted the key: QA mode on for this browser session. */
export function qaVerified(key: string) {
  ss.set(OK, key)
  state = load()
  emit()
}
/** Wrong key, ?qa=off or "Leave QA mode": forget the key and every override. */
export function endQa() {
  ls.set(KEY, null)
  ls.set(STATE, null)
  ss.set(OK, null)
  state = OFF
  emit()
}
